
import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import API from "./api";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { PharmacyProvider } from "./context/PharmacyContext";
import ReturnsHistoryPage from "./pages/ReturnsHistoryPage.jsx";


// ---------- Pages ----------
import LandingPage from "./pages/LandingPage.jsx";
import DamageManagementPage from './pages/DamageManagementPage';

import LoginPage from "./pages/LoginPage";
import AdminSendNotification from "./pages/admin/AdminSendNotification";
import RegisterPage from "./pages/RegisterPage";
import OTPVerificationPage from "./pages/OTPVerificationPage";
import DashboardPage from "./pages/DashboardPage";
import StaffDashboard from "./pages/StaffDashboard"; // ✅ NEW
import ActivityLogPage from "./pages/ActivityLogPage";
import PartiesPage from "./pages/PartiesPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TransactionsPage from "./pages/TransactionsPage";
import PaymentFailedPage from "./pages/PaymentFailedPage";
import SearchPage from "./pages/SearchPage";
import MedicinesPage from "./pages/MedicinesPage";
import Layout from "./pages/Layout.jsx";
import ProfilePage from "./pages/ProfilePage";
import ExpiryMedicinesPage from "./pages/ExpiryMedicinesPage.jsx";
import BillingPage from "./pages/BillingPage";
import SalesPage from "./pages/SalesPage";
import ContactUsPage from "./pages/ContactUsPage";
import StaffManagementPage from "./pages/StaffManagementPage";
import PurchasesPage from "./pages/PurchasesPage.jsx";
import ReportsPage from "./pages/ReportsPage.jsx";

import AdminLayout from "./pages/admin/AdminLayout.jsx";
import AdminDashboard from "./pages/admin/AdminDashboard.jsx";
import AdminUsersPage from "./pages/admin/AdminUsersPage.jsx";
import AdminUserTransactions from "./pages/admin/AdminUserTransactions.jsx";
import AdminUserParties from "./pages/AdminUserParties";
import AdminProfile from "./pages/admin/AdminProfile";
import AdminContactsPage from "./pages/admin/AdminContactsPage";
import AdminLandingPage from "./pages/admin/AdminLandingPage";

import SubscriptionPage from "./pages/SubscriptionPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import AdminSubscriptionPlans from "./pages/admin/AdminSubscriptionPlans";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminPayments from "./pages/admin/AdminPayments";

// Loading Spinner
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    flexDirection: 'column',
    gap: '20px'
  }}>
    <div style={{
      width: '50px',
      height: '50px',
      border: '4px solid #e2e8f0',
      borderTop: '4px solid #667eea',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    <p style={{ color: '#64748b' }}>Loading...</p>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Access Denied Component
const AccessDenied = ({ message }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    textAlign: 'center',
    padding: '40px'
  }}>
    <div style={{ fontSize: '60px', marginBottom: '20px' }}>🔒</div>
    <h2 style={{ color: '#1e293b', marginBottom: '10px' }}>Access Denied</h2>
    <p style={{ color: '#64748b' }}>{message || "You don't have permission to access this page."}</p>
  </div>
);

// ✅ Smart Dashboard Router - Shows Staff Dashboard for staff, regular for admin, or selected pharmacy for superadmin
function SmartDashboard() {
  const { user, isStaff, hasPermission, isSuperAdmin } = useAuth();
  
  if (isSuperAdmin) {
    const selectedTenant = localStorage.getItem('selectedTenantId');
    if (selectedTenant) {
      return <DashboardPage />;
    }
    return <Navigate to="/admin/dashboard" replace />;
  }

  // If user is staff, show Staff Dashboard
  if (isStaff) {
    return <StaffDashboard />;
  }
  
  // If user has dashboard permission, show regular dashboard
  if (hasPermission('dashboard')) {
    return <DashboardPage />;
  }
  
  // Fallback - shouldn't happen normally
  return <StaffDashboard />;
}

// Protected Route with Permission Check
function ProtectedRoute({ children, permission }) {
  const { loading, isAuthenticated, hasPermission, isSuperAdmin } = useAuth();

  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (isSuperAdmin) {
    const selectedTenant = localStorage.getItem('selectedTenantId');
    if (!selectedTenant) {
      return <Navigate to="/admin/dashboard" replace />;
    }
    return children;
  }
  
  if (permission && !hasPermission(permission)) {
    return <AccessDenied message={`You need "${permission}" permission to access this page.`} />;
  }
  
  return children;
}

// Require Auth
function RequireAuth({ children }) {
  const { loading, isAuthenticated, isSuperAdmin } = useAuth();

  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  
  if (isSuperAdmin) {
    const selectedTenant = localStorage.getItem('selectedTenantId');
    if (!selectedTenant) {
      return <Navigate to="/admin/dashboard" replace />;
    }
  }
  return children;
}

// Require Super Admin
function RequireSuperAdmin({ children }) {
  const { loading, isAuthenticated, isSuperAdmin } = useAuth();

  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) {
    toast.error("Please login to continue.");
    return <Navigate to="/login" replace />;
  }
  if (!isSuperAdmin) {
    toast.error("Access denied. Super Admin only.");
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Require Admin
function RequireAdmin({ children }) {
  const { loading, isAuthenticated, user } = useAuth();

  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) {
    toast.error("Please login to continue.");
    return <Navigate to="/login" replace />;
  }
  
  if (user?.role === 'staff') {
    toast.error("Access denied. Admin only.");
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

function PublicRoute({ children }) {
  const { loading, isAuthenticated, isSuperAdmin } = useAuth();

  if (loading) return <LoadingSpinner />;
  if (isSuperAdmin) return <Navigate to="/admin/dashboard" replace />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
}

// App Content with Interceptor
function AppContent() {
  const { logout } = useAuth();

  useEffect(() => {
    const interceptor = API.interceptors.response.use(
      (response) => response,
      (error) => {
        const isLoginRequest = error.config?.url?.includes('/login');
        const isVerifyRequest = error.config?.url?.includes('/verify');

        if (error.response?.status === 403 && !isLoginRequest && !isVerifyRequest) {
          const errorCode = error.response?.data?.code;
          
          if (errorCode === 'STAFF_INACTIVE') {
            toast.error("Your staff account has been deactivated.");
            logout();
            setTimeout(() => {
              window.location.hash = '#/login';
              window.location.reload();
            }, 1000);
            return Promise.reject(error);
          }
          
          console.warn("Access Forbidden: Logging out");
          logout();
          
          if (!toast.isActive("session-expired")) {
            toast.error("Session expired. Please login again.", { toastId: "session-expired" });
          }

          setTimeout(() => {
            window.location.hash = '#/login';
            window.location.reload();
          }, 1000);
        }
        return Promise.reject(error);
      }
    );

    return () => API.interceptors.response.eject(interceptor);
  }, [logout]);

  return (
    <>
      <ToastContainer position="bottom-center" autoClose={3000} newestOnTop closeOnClick pauseOnHover draggable />

      <Routes>
        <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
        
        {/* Auth Routes */}
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/verify-otp" element={<OTPVerificationPage />} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
        <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />

        {/* User Routes */}
        <Route element={<RequireAuth><Layout /></RequireAuth>}>
          {/* ✅ Smart Dashboard - Shows Staff Dashboard for staff */}
          <Route path="/dashboard" element={<SmartDashboard />} />
          
          <Route path="/parties" element={
            <ProtectedRoute permission="parties"><PartiesPage /></ProtectedRoute>
          } />
          <Route path="/transactions" element={
            <ProtectedRoute permission="transactions"><TransactionsPage /></ProtectedRoute>
          } />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/medicines" element={
            <ProtectedRoute permission="medicines"><MedicinesPage /></ProtectedRoute>
          } />
          <Route path="/expiry" element={
            <ProtectedRoute permission="expiry"><ExpiryMedicinesPage /></ProtectedRoute>
          } />
          <Route path="/payment-failed" element={<PaymentFailedPage />} />
          <Route path="/billing" element={
            <ProtectedRoute permission="billing"><BillingPage /></ProtectedRoute>
          } />
          <Route path="/damages" element={
            <ProtectedRoute permission="damages"><DamageManagementPage /></ProtectedRoute>
          } />
          <Route path="/sales" element={
            <ProtectedRoute permission="sales"><SalesPage /></ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute permission="profile"><ProfilePage /></ProtectedRoute>
          } />
          <Route path="/contact" element={
            <ProtectedRoute permission="contact"><ContactUsPage /></ProtectedRoute>
          } />
          <Route path="/activity-logs" element={
            <ProtectedRoute permission="view_activity_logs"><ActivityLogPage /></ProtectedRoute>
          } />  \
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/purchases" element={
            <ProtectedRoute permission="purchases"><PurchasesPage /></ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute permission="reports"><ReportsPage /></ProtectedRoute>
          } />
          <Route path="/returns" element={
  <ProtectedRoute permission="purchases"><ReturnsHistoryPage /></ProtectedRoute>
} />
          <Route path="/payment-cancelled" element={
            <div className="p-10 text-center"><h1 className="text-2xl font-bold">Payment Cancelled</h1></div>
          } />
          
          {/* Staff Management - Admin Only */}
          <Route path="/staff" element={
            <RequireAdmin><StaffManagementPage /></RequireAdmin>
          } />
        </Route>

        {/* Super Admin Routes */}
        <Route element={<RequireSuperAdmin><AdminLayout /></RequireSuperAdmin>}>
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/user-parties" element={<AdminUserParties />} />
          <Route path="/admin/user-transactions" element={<AdminUserTransactions />} />
          <Route path="/admin/landing-page" element={<AdminLandingPage />} />
          <Route path="/admin/profile" element={<AdminProfile />} />
          <Route path="/admin/contacts" element={<AdminContactsPage />} />
          <Route path="/admin/notifications/send" element={<AdminSendNotification />} />
          <Route path="/admin/subscription-plans" element={<AdminSubscriptionPlans />} />
          <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
          <Route path="/admin/payments" element={<AdminPayments />} />
        </Route>

        <Route path="*" element={
          <div className="p-10 text-center">
            <h1 className="text-4xl font-bold">404 - Page Not Found</h1>
          </div>
        } />
      </Routes>
    </>
  );
}

// Main App
export default function App() {
  return (
    <AuthProvider>
      <PharmacyProvider>
        <AppContent />
      </PharmacyProvider>
    </AuthProvider>
  );
}